"use client"

import * as React from "react"
import {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
} from '../../components/card'
import { cn } from '../../lib/utils'

// Re-export base card components with UI* naming convention
export const UICard = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof Card> & { interactive?: boolean }
>(({ className, interactive = false, ...props }, ref) => {
  return (
    <Card
      ref={ref}
      className={cn(
        // Background with refined transparency (improved contrast)
        "bg-background/80 dark:bg-gray-900/50",
        // Clean, visible borders (Apple-style)
        "border border-gray-200 dark:border-gray-800",
        // Subtle inner glow for glass effect
        "ring-1 ring-white/10 dark:ring-white/5",
        // Refined shadow system
        "shadow-sm shadow-gray-950/5 dark:shadow-black/20",
        // Single consolidated transition (overrides base transition-[background-color,box-shadow])
        "!transition-[transform,translate,scale,background-color,box-shadow,border-color] duration-300 ease-out",
        // Hover state only when interactive
        interactive && [
          "hover:-translate-y-0.5 hover:scale-[1.005]",
          "hover:bg-background/80 dark:hover:bg-background/60",
          "hover:shadow-md hover:shadow-gray-950/10 dark:hover:shadow-black/30",
          "hover:border-gray-300 dark:hover:border-gray-700",
          "hover:ring-2 hover:ring-white/20 dark:hover:ring-white/10",
        ],
        className
      )}
      {...props}
    />
  )
})

export const UICardHeader = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof CardHeader>
>(({ className, ...props }, ref) => {
  return <CardHeader ref={ref} className={className} {...props} />
})

export const UICardTitle = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof CardTitle>
>(({ className, ...props }, ref) => {
  return <CardTitle ref={ref} className={className} {...props} />
})

export const UICardDescription = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof CardDescription>
>(({ className, ...props }, ref) => {
  return <CardDescription ref={ref} className={cn("text-muted-foreground dark:text-gray-400", className)} {...props} />
})

export const UICardAction = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof CardAction>
>(({ className, ...props }, ref) => {
  return <CardAction ref={ref} className={className} {...props} />
})

export const UICardContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof CardContent>
>(({ className, ...props }, ref) => {
  return <CardContent ref={ref} className={className} {...props} />
})

export const UICardFooter = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof CardFooter>
>(({ className, ...props }, ref) => {
  return <CardFooter ref={ref} className={className} {...props} />
})

// Set display names for debugging
UICard.displayName = "UICard"
UICardHeader.displayName = "UICardHeader"
UICardTitle.displayName = "UICardTitle"
UICardDescription.displayName = "UICardDescription"
UICardAction.displayName = "UICardAction"
UICardContent.displayName = "UICardContent"
UICardFooter.displayName = "UICardFooter"

// Export types
export type UICardProps = React.ComponentProps<typeof Card> & { interactive?: boolean }
export type UICardHeaderProps = React.ComponentProps<typeof CardHeader>
export type UICardTitleProps = React.ComponentProps<typeof CardTitle>
export type UICardDescriptionProps = React.ComponentProps<typeof CardDescription>
export type UICardActionProps = React.ComponentProps<typeof CardAction>
export type UICardContentProps = React.ComponentProps<typeof CardContent>
export type UICardFooterProps = React.ComponentProps<typeof CardFooter>